import type { Frame, InstanceFrame, MainFrame } from './types';

/**
 * Resolve any Frame to its display HTML.
 *
 * - FreeFrame  → its own html
 * - MainFrame  → its html with text slots at their Main-declared defaults,
 *                nested cf-instance refs expanded.
 * - InstanceFrame → its Main's html with text slots filled from slotValues,
 *                   nested cf-instance refs expanded.
 *
 * Cycle detection: `seen` carries the chain of Main ids being expanded.
 * If a cf-instance ref would re-enter a Main already in the chain, the node
 * is replaced with a stub instead of recursing.
 *
 * Pure function. Uses DOMParser via <template> (jsdom in tests).
 */
export function resolveInstance(
  frame: Frame,
  byId: Map<string, Frame>,
  seen: Set<string> = new Set(),
): string {
  if (frame.kind === 'free') return frame.html;

  if (frame.kind === 'main') {
    // Render Main as itself — defaults for slots, plus expand nested refs.
    return expand(frame.html, byId, new Set([...seen, frame.id]), {});
  }

  if (frame.kind === 'instance') {
    const main = byId.get(frame.mainId);
    if (!main || main.kind !== 'main') {
      return missingStub(`missing Main: ${frame.mainId}`);
    }
    if (seen.has(main.id)) {
      return cycleStub(main.title || main.id);
    }
    return expand(main.html, byId, new Set([...seen, main.id]), frame.slotValues);
  }

  // Tool frames (chat / code / fx) have no display html — rendered by React
  // components, not via this fn. Return empty.
  return '';
}

/** Substitute data-slot text + recursively expand cf-instance refs. */
function expand(
  html: string,
  byId: Map<string, Frame>,
  seen: Set<string>,
  slotValues: { [k: string]: string },
): string {
  if (typeof document === 'undefined') return html;
  const tmpl = document.createElement('template');
  tmpl.innerHTML = html;
  const host = document.createElement('div');
  host.appendChild(tmpl.content);

  // 1) text slots — substitute innerText where the instance has a value
  for (const el of Array.from(host.querySelectorAll('[data-slot]'))) {
    if (isInsideCfInstance(el)) continue;
    const name = el.getAttribute('data-slot');
    if (name && slotValues[name] !== undefined) {
      el.textContent = slotValues[name];
    }
  }

  // 2) nested cf-instance refs — recursively expand
  for (const node of Array.from(host.querySelectorAll('cf-instance'))) {
    const ref = node.getAttribute('ref');
    if (!ref) {
      replaceWith(node, missingStub('cf-instance missing ref'));
      continue;
    }
    const target = byId.get(ref);
    if (!target || target.kind !== 'main') {
      replaceWith(node, missingStub(`missing Main: ${ref}`));
      continue;
    }
    if (seen.has(target.id)) {
      replaceWith(node, cycleStub(target.title || target.id));
      continue;
    }
    const slotMap = readSlotAttrs(node);
    const pseudo: InstanceFrame = synthInstance(target, slotMap);
    const childHtml = resolveInstance(pseudo, byId, seen);
    replaceWith(node, childHtml);
  }

  return host.innerHTML;
}

function readSlotAttrs(node: Element): { [k: string]: string } {
  const out: { [k: string]: string } = {};
  for (const attr of Array.from(node.attributes)) {
    if (attr.name.startsWith('slot:')) {
      out[attr.name.slice('slot:'.length)] = attr.value;
    }
  }
  return out;
}

function synthInstance(main: MainFrame, slotValues: { [k: string]: string }): InstanceFrame {
  return {
    kind: 'instance',
    id: `__nested__${main.id}`,
    x: 0, y: 0, w: 0, z: 0,
    title: '',
    mainId: main.id,
    slotValues,
    effect: { kind: 'none' },
  };
}

function replaceWith(node: Element, html: string): void {
  const tmpl = node.ownerDocument!.createElement('template');
  tmpl.innerHTML = html;
  node.replaceWith(tmpl.content);
}

function isInsideCfInstance(el: Element): boolean {
  let p: Element | null = el.parentElement;
  while (p) {
    if (p.tagName.toLowerCase() === 'cf-instance') return true;
    p = p.parentElement;
  }
  return false;
}

function missingStub(msg: string): string {
  return `<div class="cf-missing" data-cf-error="missing">${escapeHtml(msg)}</div>`;
}

function cycleStub(name: string): string {
  return `<span class="cf-cycle" data-cf-error="cycle">↻ cycle: ${escapeHtml(name)}</span>`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]!));
}
